import { cn } from '@/internationalization/cn'

export interface SegmentedOption<T extends string> {
  value: T
  label: string
}

export interface SegmentedControlProps<T extends string> {
  options: SegmentedOption<T>[]
  value: T
  onChange: (value: T) => void
  size?: 'sm' | 'md'
  className?: string
  /** Announced to assistive tech — the group needs a name. */
  label: string
}

/**
 * Two to four mutually exclusive views: date range, chart granularity, list
 * density. More options than that, or options that need icons, means a
 * Dropdown; a segmented control has to fit on one line at 375px.
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  size = 'sm',
  className,
  label,
}: SegmentedControlProps<T>) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn('inline-flex items-center rounded-md bg-surface-muted p-0.5', className)}
    >
      {options.map((option) => {
        const isActive = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isActive}
            onClick={() => onChange(option.value)}
            className={cn(
              'rounded font-medium transition-colors duration-fast',
              size === 'sm' ? 'px-2.5 py-1 text-sm' : 'px-3 py-1.5 text-base',
              isActive ? 'bg-surface text-fg shadow-xs' : 'text-fg-muted hover:text-fg',
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
